import { generateClient } from 'aws-amplify/api';
import moment from 'moment';
import * as qlqueries from '../graphql/queries';
import * as customQueries from '../graphql/customQueries';
import * as routerQueries from '../graphql/routerQueries';
import { isCurrentEventLive, monitoringStateOf } from '../services/eventPhase';
import { messageSeverity } from '../utils/mediaMessages';

const client = generateClient();

const parseDataJson = (dataJson) => {
  if (!dataJson) return null;
  try {
    return JSON.parse(dataJson);
  } catch (error) {
    console.error('Failed to parse dataJson:', error, dataJson);
    return null;
  }
};

const parseEventConfig = (configValue) => {
  if (!configValue) return {};
  if (typeof configValue === 'object') return { ...configValue };
  if (typeof configValue !== 'string') return {};
  try {
    return JSON.parse(configValue);
  } catch (error) {
    console.warn('Failed to parse event config', error);
    return {};
  }
};

const enrichMonitoringEvent = (event, monitoringState) => {
  const config = parseEventConfig(event.config);
  const inputIds = [event.input?.id, event.inputOverride?.id].filter(Boolean);
  const outputIds = (event.outputs?.items || [])
    .map((outputMap) => outputMap?.output?.id)
    .filter(Boolean);

  return {
    ...event,
    monitoringState,
    liveMonitoring: config.liveMonitoring === true,
    // Per-booking replacement for InputProfile.config - same shape, written by AgendaPreview.
    inputProfileOverride: config.inputProfileOverride && typeof config.inputProfileOverride === 'object'
      ? config.inputProfileOverride
      : null,
    monitoredInputIds: inputIds,
    monitoredOutputIds: Array.from(new Set(outputIds)),
  };
};

const state = {
  inputs: [],
  inputInfos: {},
  outputs: [],
  outputInfos: {},
  monitoredEvents: [],
  liveInputIds: [],
  liveOutputIds: [],
  // resourceId -> eventId of the live event it belongs to (for "open report").
  liveInputEventId: {},
  liveOutputEventId: {},
  loading: false,
  loadingStatuses: false,
  loadingLiveAssignments: false,
  // Last events-fetch failure, so an empty list can say why instead of looking normal.
  eventsError: null,
};

const getters = {
  inputsByType: (state) => (type) => {
    if (!type) return state.inputs;
    return state.inputs.filter((input) => input.type === type);
  },

  availableTypes: (state) => {
    const types = new Set(state.inputs.map((input) => input.type));
    return Array.from(types).sort();
  },

  getInputInfo: (state) => (inputId) => state.inputInfos[inputId] || null,

  isInputHealthy: (state) => (inputId) => {
    const info = state.inputInfos[inputId];
    if (!info) return null;
    // MEDIA_CONNECT format
    if (info.Messages && info.Messages.length > 0) {
      return !info.Messages.some((msg) => messageSeverity(msg) === 'error');
    }
    // ROUTE format - no error mechanism, healthy if status exists
    if (info.status) return true;
    // MEDIA_CONNECT with no messages
    if (info.State) return true;
    return null;
  },

  getInputState: (state) => (inputId) => {
    const info = state.inputInfos[inputId];
    if (!info) return null;
    // MEDIA_CONNECT: State (PascalCase)
    if (info.State) return info.State;
    // ROUTE: status.status (camelCase)
    if (info.status?.status) return info.status.status.toUpperCase();
    return null;
  },

  // Output getters
  outputsByType: (state) => (type) => {
    if (!type) return state.outputs;
    return state.outputs.filter((output) => output.outputType === type);
  },

  availableOutputTypes: (state) => {
    const types = new Set(state.outputs.map((output) => output.outputType));
    return Array.from(types).sort();
  },

  getOutputInfo: (state) => (outputId) => state.outputInfos[outputId] || null,

  isOutputHealthy: (state) => (outputId) => {
    const info = state.outputInfos[outputId];
    if (!info) return null;
    // MediaLive output — health is determined by channel State
    const mlState = info.additionalInfo?.mediaLiveChannel?.State;
    if (mlState) return mlState === 'RUNNING';
    // MediaConnect output
    if (info.Messages && info.Messages.length > 0) {
      return !info.Messages.some((msg) => messageSeverity(msg) === 'error');
    }
    if (info.State) return true;
    return null;
  },

  getOutputState: (state) => (outputId) => {
    const info = state.outputInfos[outputId];
    if (!info) return null;
    // MediaLive output — expose the channel State
    if (info.additionalInfo?.mediaLiveChannel?.State) return info.additionalInfo.mediaLiveChannel.State;
    if (info.State) return info.State;
    return null;
  },

  liveInputIds: (state) => state.liveInputIds,

  liveOutputIds: (state) => state.liveOutputIds,

  monitoredEvents: (state) => state.monitoredEvents,

  // Live event id a monitored input/output belongs to, for deep-linking to its
  // analytics report.
  getInputEventId: (state) => (inputId) => state.liveInputEventId[inputId] || null,

  getOutputEventId: (state) => (outputId) => state.liveOutputEventId[outputId] || null,
};

const mutations = {
  setInputs: (state, payload) => {
    state.inputs = payload.items || [];
  },

  setInputInfos: (state, payload) => {
    state.inputInfos = { ...state.inputInfos, ...payload };
  },

  setOutputs: (state, payload) => {
    state.outputs = payload.items || [];
  },

  setMonitoredEvents: (state, payload) => {
    state.monitoredEvents = payload || [];
  },

  setOutputInfos: (state, payload) => {
    state.outputInfos = { ...state.outputInfos, ...payload };
  },

  setLiveAssignments: (state, {
    inputIds, outputIds, inputEventId, outputEventId,
  }) => {
    state.liveInputIds = inputIds || [];
    state.liveOutputIds = outputIds || [];
    state.liveInputEventId = inputEventId || {};
    state.liveOutputEventId = outputEventId || {};
  },

  setLoading: (state, payload) => {
    state.loading = payload;
  },

  setLoadingStatuses: (state, payload) => {
    state.loadingStatuses = payload;
  },

  setLoadingLiveAssignments: (state, payload) => {
    state.loadingLiveAssignments = payload;
  },

  setEventsError: (state, payload) => {
    state.eventsError = payload;
  },
};

const actions = {
  async fetchInputs({ commit }) {
    commit('setLoading', true);
    try {
      const result = await client.graphql({
        query: qlqueries.listInputsRouterAlt,
        variables: { limit: 1500 },
      });
      const { data } = result;
      commit('setInputs', data.listInputs);
      return data.listInputs;
    } catch (error) {
      console.error('SourceMonitoring fetchInputs error', error);

      const hasData = !!error.data && !!error.data.listInputs;
      if (hasData) {
        const { data } = error;
        commit('setInputs', data.listInputs);
        this._vm.$message.error({
          message: "Oops something went wrong. Can't get some of the inputs",
        });
        return { error: true, message: error.errors };
      }

      this._vm.$message.error({
        message: "Oops something went wrong. Can't get inputs",
      });
      return { error: true, message: error.errors };
    } finally {
      commit('setLoading', false);
    }
  },

  async fetchInputInfos({ commit, state }, inputIds) {
    const ids = inputIds || state.inputs.map((input) => input.id);
    if (ids.length === 0) return;

    commit('setLoadingStatuses', true);
    try {
      const result = await client.graphql({
        query: routerQueries.routerInputsTargetInfo,
        variables: { inputIds: ids },
      });

      const response = result?.data?.routerInputsTargetInfo;
      if (response && response.success) {
        const parsed = parseDataJson(response.dataJson);
        if (parsed) {
          const infosMap = {};
          if (Array.isArray(parsed)) {
            parsed.forEach((info) => {
              if (info.id) {
                infosMap[info.id] = info;
              }
            });
          } else if (typeof parsed === 'object') {
            Object.assign(infosMap, parsed);
          }
          commit('setInputInfos', infosMap);
        }
      }
    } catch (error) {
      console.error('SourceMonitoring fetchInputInfos error', error);
      this._vm.$message.error({
        message: "Oops something went wrong. Can't get input statuses",
      });
    } finally {
      commit('setLoadingStatuses', false);
    }
  },

  async fetchOutputs({ commit }) {
    commit('setLoading', true);
    try {
      const result = await client.graphql({
        query: qlqueries.listOutputsRouterAlt,
        variables: { limit: 1500 },
      });
      const { data } = result;
      commit('setOutputs', data.listOutputs);
      return data.listOutputs;
    } catch (error) {
      console.error('SourceMonitoring fetchOutputs error', error);

      const hasData = !!error.data && !!error.data.listOutputs;
      if (hasData) {
        const { data } = error;
        commit('setOutputs', data.listOutputs);
        this._vm.$message.error({
          message: "Oops something went wrong. Can't get some of the outputs",
        });
        return { error: true, message: error.errors };
      }

      this._vm.$message.error({
        message: "Oops something went wrong. Can't get outputs",
      });
      return { error: true, message: error.errors };
    } finally {
      commit('setLoading', false);
    }
  },

  async fetchOutputInfos({ commit, state }, outputIds) {
    const mediaConnectOutputIds = new Set(
      state.outputs
        .filter((output) => ['MEDIA_CONNECT', 'MEDIA_LIVE'].includes(output.outputType))
        .map((output) => output.id),
    );
    // Callers outside the monitoring view (event analytics) never load the
    // outputs catalogue, so an explicit id list is trusted as-is when it's empty.
    const ids = (outputIds || Array.from(mediaConnectOutputIds))
      .filter((id) => mediaConnectOutputIds.size === 0 || mediaConnectOutputIds.has(id));
    if (ids.length === 0) return;

    commit('setLoadingStatuses', true);
    try {
      const result = await client.graphql({
        query: routerQueries.routerOutputsTargetInfo,
        variables: { outputIds: ids },
      });

      const response = result?.data?.routerOutputsTargetInfo;
      if (response && response.success) {
        const parsed = parseDataJson(response.dataJson);
        if (parsed) {
          const infosMap = {};
          if (Array.isArray(parsed)) {
            parsed.forEach((info) => {
              if (info.id) {
                infosMap[info.id] = info;
              }
            });
          } else if (typeof parsed === 'object') {
            Object.assign(infosMap, parsed);
          }
          commit('setOutputInfos', infosMap);
        }
      }
    } catch (error) {
      console.error('SourceMonitoring fetchOutputInfos error', error);
      this._vm.$message.error({
        message: "Oops something went wrong. Can't get output statuses",
      });
    } finally {
      commit('setLoadingStatuses', false);
    }
  },

  async fetchLiveAssignments({ commit }) {
    commit('setLoadingLiveAssignments', true);

    try {
      const liveInputIds = new Set();
      const liveOutputIds = new Set();
      const monitoredEvents = [];
      // resourceId -> eventId so the UI can deep-link a resource to its report.
      const liveInputEventId = {};
      const liveOutputEventId = {};
      const now = moment.utc();
      const startTime = now.clone().subtract(4, 'days').toISOString();
      const endTime = now.clone().add(4, 'days').toISOString();

      const fetchPage = async (nextToken = null) => {
        const variables = {
          startTime,
          endTime,
          limit: 1000,
          nextToken,
        };

        let data;
        try {
          const result = await client.graphql({
            query: customQueries.sourceMonitoringEventsByStartTime,
            variables,
          });
          data = result.data;
        } catch (error) {
          // Field-level authorization failures arrive as errors *with* usable data
          // attached - dropping the whole page over one unreadable field blanks the list.
          if (!error?.data?.eventsByStartTime) throw error;
          console.warn('SourceMonitoring fetchLiveAssignments partial data', error.errors);
          data = error.data;
        }

        const events = data?.eventsByStartTime?.items || [];
        const newNextToken = data?.eventsByStartTime?.nextToken || null;

        // Everything in the fetched window is kept so the list is not empty off-air; the
        // phase filters in the UI decide what is shown.
        events.filter(Boolean).forEach((event) => {
          monitoredEvents.push(enrichMonitoringEvent(event, monitoringStateOf(event, now)));

          // The live resource maps follow the booking status, not the UI phase: a booking
          // in its pre-live window is still on air, and its report link must keep working.
          if (!isCurrentEventLive(event)) {
            return;
          }

          if (event.input?.id) {
            liveInputIds.add(event.input.id);
            liveInputEventId[event.input.id] = event.id;
          }

          if (event.inputOverride?.id) {
            liveInputIds.add(event.inputOverride.id);
            liveInputEventId[event.inputOverride.id] = event.id;
          }

          event.outputs?.items?.filter(Boolean).forEach((outputMap) => {
            if (outputMap.output?.id) {
              liveOutputIds.add(outputMap.output.id);
              liveOutputEventId[outputMap.output.id] = event.id;
            }
          });
        });

        if (newNextToken) {
          await fetchPage(newNextToken);
        }
      };

      await fetchPage();

      commit('setLiveAssignments', {
        inputIds: Array.from(liveInputIds),
        outputIds: Array.from(liveOutputIds),
        inputEventId: liveInputEventId,
        outputEventId: liveOutputEventId,
      });
      commit('setMonitoredEvents', monitoredEvents);
      commit('setEventsError', null);
    } catch (error) {
      console.error('SourceMonitoring fetchLiveAssignments error', error);
      const detail = error?.errors?.[0]?.message || error?.message || 'Unknown error';
      commit('setEventsError', detail);
      this._vm.$message.error({
        message: `Oops something went wrong. Can't get events: ${detail}`,
      });
    } finally {
      commit('setLoadingLiveAssignments', false);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
